// /translate/visa-docs — Visa document translation pack hub (TH)
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("visa-docs")!;

export const Route = createFileRoute("/translate/visa-docs")({
  head: () => {
    const url = `${SITE}/translate/visa-docs`;
    const title = `${cert.nameTh} | NYC Language Institute`;
    const desc = cert.taglineTh;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/visa-docs", "th") }, { name: "description", content: ctaDesc(desc, "/translate/visa-docs", "th")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/visa-docs", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/visa-docs", "th")},
        { property: "og:url", content: url },
      ],
      links: canonicalAndHreflang("/translate/visa-docs", "/en/translate/visa-docs"),
      scripts: [
        speakableSchema(),
        howToSchema({
          name: "ขั้นตอนแปลชุดเอกสารวีซ่ากับ NYC",
          description: cert.taglineTh,
          totalTime: "PT3D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsTh.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate/visa-docs", serviceType: "Visa Document Translation Pack", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: cert.faqsTh.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: "เอกสารวีซ่า", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — MCP Server",
          description: "AI agent endpoint exposing visa document translation tooling.",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="th" />,
});
